import { useState, useEffect, useRef, useCallback } from 'react'
import { useRequests } from '../requests/useRequests'
import { Message, Attachment } from '../types/messages'

export const useMessages = (conversationId: number | null, updateUnreadCount: (id: number) => void) => {
  const requests = useRequests()
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [newMessage, setNewMessage] = useState<string>('')

  // Referencja do poprzedniej konwersacji, aby uniknąć zbędnych wywołań
  const prevConversationRef = useRef<number | null>(null)
  // Dodajemy referencję do śledzenia, czy już oznaczono wiadomości jako przeczytane
  const markedAsReadRef = useRef<Set<number>>(new Set())

  // Dodajemy referencję do funkcji updateUnreadCount, aby przerwać pętlę aktualizacji
  const updateUnreadCountRef = useRef(updateUnreadCount)

  // Aktualizujemy referencję gdy zmienia się updateUnreadCount
  useEffect(() => {
    updateUnreadCountRef.current = updateUnreadCount
  }, [updateUnreadCount])

  // Pobieranie wiadomości dla wybranej konwersacji
  const fetchMessages = useCallback(async (convId: number) => {
    // Pomijamy pobieranie, jeśli to ta sama konwersacja co poprzednio
    if (prevConversationRef.current === convId) return

    try {
      setLoading(true)
      const response = await requests.getConversationMessages(convId)
      setMessages(response.data.slice().reverse())

      // Aktualizujemy referencję tylko po pomyślnym pobraniu danych
      prevConversationRef.current = convId

      // Oznacz wiadomości jako przeczytane tylko jeśli nie zostały jeszcze oznaczone
      if (!markedAsReadRef.current.has(convId)) {
        await requests.markConversationAsRead(convId)

        // Używamy referencji do funkcji updateUnreadCount zamiast bezpośrednio funkcji
        updateUnreadCountRef.current(convId)

        // Zapisz informację, że oznaczyliśmy już tę konwersację
        markedAsReadRef.current.add(convId)
      }
    } catch (err) {
      console.error('Błąd podczas pobierania wiadomości:', err)
      setError('Nie udało się pobrać wiadomości. Spróbuj ponownie później.')
    } finally {
      setLoading(false)
    }
  }, [requests]) // Usunięto updateUnreadCount z zależności

  useEffect(() => {
    if (conversationId === null) {
      // Resetuj wiadomości, gdy nie wybrano konwersacji
      setMessages([])
      return
    }

    fetchMessages(conversationId)

    // Funkcja czyszcząca
    return () => {
      // Jeśli component się odmontuje podczas zmiany konwersacji,
      // możemy przerwać oczekujące operacje lub wykonać czyszczenie
    }
  }, [conversationId, fetchMessages])

  // Pobranie załącznika
  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const downloadUrl = attachment.presignedUrl || attachment.url

      // Pobierz plik jako blob
      const response = await fetch(downloadUrl)
      const blob = await response.blob()

      // Utwórz URL dla blob
      const blobUrl = window.URL.createObjectURL(blob)

      // Utwórz i skonfiguruj link do pobrania
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = attachment.name
      link.style.display = 'none'

      // Dodaj link do dokumentu, kliknij go i usuń
      document.body.appendChild(link)
      link.click()

      // Wyczyść URL i usuń link
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(link)
    } catch (error) {
      console.error('Błąd podczas pobierania załącznika:', error)
      throw new Error('Nie udało się pobrać załącznika')
    }
  }

  return {
    messages,
    setMessages,
    loading,
    error,
    newMessage,
    setNewMessage,
    handleDownloadAttachment
  }
}
